import {makeOwnDisplayName} from '../../../adjustment-pricing-grid/services/portfolio-groups';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter, OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {ServiceConfiguration} from "../../../adjustment-pricing-grid/services/ServiceConfiguration";
import {SessionService} from "../../../authentication/session-service.service";
import {CashFlowPortfoliosService} from "../../../adjustment-pricing-grid/services/cashflow-portfolios.service";
import {ApgPortfolio, findDefaultPortfolio} from "../../../adjustment-pricing-grid/model/ApgPortfolio";
import {UserDto} from "../../../authentication/dtos/auth-result-dto.inteface";
import {arraysEqual, DetectMethodChanges, DxValueChanged, isVoid, makePortfolioKey} from "../../../utils";
import {HgOriginalClientPositionsService} from "./hg-original-client-positions.service";
import {HedgePosition} from "../../data-model/hedge-position";
import {HedgePositionsService} from "../hedge-positions/hedge-positions.service";
import {MessageBusService} from "../../../message-bus.service";
import {
    HgHedgePositionsSyncDialogComponent
} from "../hedge-positions-sync-dialog/hg-hedge-positions-sync-dialog.component";
import {Subscription} from "rxjs";
import {ApgDataService} from "../../../adjustment-pricing-grid/services/apg-data.service";
import {Toast, ToastrService} from "ngx-toastr";
import {filter} from "rxjs/operators";
import {HgIndependentPortfoliosService} from "./hg-independent-portfolios.service";
import {HgCreatePortfolioDialogComponent} from "./hg-create-portfolio-dialog.component";
import {
    GenericConfirmationDialogComponent
} from "../../../generic-confirmation-dialog/generic-confirmation-dialog.component";

@Component({
    selector: 'ets-hg-portfolio-selector',
    templateUrl: 'hg-portfolio-selector.component.html',
    styleUrls:[
        './hg-portfolio-selector.component.scss',
        '../../hedging-grid-common-styles.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
    ]
})

export class HgPortfolioSelectorComponent implements OnInit, OnDestroy, AfterViewInit {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _sessionService: SessionService,
        private readonly _portfoliosService: CashFlowPortfoliosService,
        private readonly _clientPositionsService: HgOriginalClientPositionsService,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _messageBus: MessageBusService,
        private readonly _apgDataService: ApgDataService,
        private readonly _independentPortfoliosService: HgIndependentPortfoliosService,
        private readonly _toastr: ToastrService
    ) {
    }

    private _subscriptions : Subscription[] = [];

    isDataLoading: boolean;

    selectedPortfolio: ApgPortfolio;

    portfolios: { key: string, userId: string, items: ApgPortfolio[] }[];

    get canRefreshPortfolio(): boolean {
        return !isVoid(this.selectedPortfolio) && !this.isOwnUserContext;
    }

    // Only INDEPENDENT, own-user books are deletable here. APG-bound portfolios
    // belong to APG and must never be removed from the Hedging Grid (CR-20260606-001).
    get canDeletePortfolio(): boolean {
        return !isVoid(this.selectedPortfolio)
            && this.selectedPortfolio.isIndependent === true
            && this.isOwnUserContext;
    }

    get isOwnUserContext(): boolean {
        return this.selectedPortfolio?.userId
            === this._sessionService.sessionData.userId;
    }

    @Output()
    portfolioSelected = new EventEmitter<ApgPortfolio>();

    @ViewChild(HgHedgePositionsSyncDialogComponent)
    portfolioSyncDialogCmp: HgHedgePositionsSyncDialogComponent;

    @ViewChild(HgCreatePortfolioDialogComponent)
    createPortfolioDialog: HgCreatePortfolioDialogComponent;

    @ViewChild('deletePortfolioDialog')
    deletePortfolioDialog: GenericConfirmationDialogComponent;

    ngOnInit() {
        this._subscriptions.push(
            this._messageBus.of('Hg.HedgePositionsChanged')
                .subscribe(() => {
                    this._changeDetector.detectChanges();
                })
        );

        this._subscriptions.push(
            this._messageBus.of<{portfolios: ApgPortfolio[]}>('Apg.PortfoliosUpdated')
                .subscribe((msg) => this.onPortfoliosUpdated(msg.payload))
        );

        this._subscriptions.push(
            this._messageBus.of<{portfolios: ApgPortfolio[]}>('Hg.IndependentPortfoliosUpdated')
                .subscribe((msg) => this.onPortfoliosUpdated(msg.payload))
        );

        this._subscriptions.push(
            this._messageBus.of<{inUse: boolean, portfolio: ApgPortfolio, sender: object}>('Hg.PortfolioIsInUse')
                .pipe(
                    filter(x => x.payload.sender !== this)
                )
                .subscribe((args) => {
                    const payload = args.payload;

                    const selectedPortfolioKey = makePortfolioKey(this.selectedPortfolio);
                    const requestPortfolioKey = makePortfolioKey(payload.portfolio);

                    payload.inUse = selectedPortfolioKey === requestPortfolioKey
                     && !isVoid(this.selectedPortfolio) && !isVoid(payload.portfolio);
                })
        );
    }

    ngOnDestroy(): void {
        this._subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    @DetectMethodChanges({isAsync: true})
    async ngAfterViewInit() {

        this.isDataLoading = true;

        try {
            await this.initPortfolios();

        } finally {

            this.isDataLoading = false;

        }
    }

    private async initPortfolios() {
        const users = this._sessionService.sessionData.users
            .sort((a, b) => a.userName.localeCompare(b.userName));

        const grps = [];

        for (const user of users) {
            const grp = {
                key: user.userName,
                userId: user.userId,
                items: []
            };

            const pfs = await this.loadAllPortfoliosForUser(user.userId, user.userName);

            grp.items.push(...pfs);

            grps.push(grp);
        }

        const ownUser = this.getUserObject(this._sessionService.sessionData.userId);

        const ownPfs = await this.loadAllPortfoliosForUser(ownUser.userId, ownUser.userName);

        const ownGrp = {
            key: ownUser.userName,
            userId: ownUser.userId,
            items: ownPfs
        }

        grps.unshift(ownGrp);

        this.portfolios = grps;

        if (!isVoid(this.portfolios)) {
            const apgPortfolios = this.portfolios.flatMap(x => x.items);
            if (apgPortfolios.length === 1) {
                setTimeout(() => {
                    this.selectedPortfolio = apgPortfolios[0];
                    this._changeDetector.detectChanges();
                }, 25);
            }
        }
    }

    private getUserObject(userId: string): UserDto {

        if (this._sessionService.sessionData.userId === userId) {

            const ownUserName = makeOwnDisplayName(this._sessionService.sessionData);

            return {
                userId,
                userName: ownUserName
            };

        } else {

            return this._sessionService.sessionData.users.find(x => x.userId === userId);
        }
    }

    private onPortfolioSelected() {
        this.portfolioSelected.emit(this.selectedPortfolio);

    }

    @DetectMethodChanges()
    onPortfolioChange(portfolio: ApgPortfolio) {
        const payload = {
            portfolio: portfolio,
            sender: this,
            inUse: undefined
        };

        this._messageBus.publish({
            topic: 'Hg.PortfolioIsInUse',
            payload
        });

        if (payload.inUse) {
            this._toastr.error('Selected Portfolio Is Already In Use By Other Open "Hedging Grid" Panel');
            return;
        }

        this.selectedPortfolio = portfolio;

        setTimeout(() => this.onPortfolioSelected());
    }

    @DetectMethodChanges({isAsync: true})
    async onCreateIndependentPortfolio() {
        const result = await this.createPortfolioDialog.show();
        if (isVoid(result)) {
            return;
        }

        const ownUser = this.getUserObject(this._sessionService.sessionData.userId);

        this._independentPortfoliosService.configure({
            userId: ownUser.userId,
            userName: ownUser.userName
        });

        const created = this._independentPortfoliosService.addPortfolio({
            name: result.name,
            underlying: result.underlying,
            defaultQty: result.defaultQty
        });

        this._messageBus.publish({
            topic: 'Hg.IndependentPortfoliosUpdated',
            payload: { portfolios: [created] }
        });

        await this.initPortfolios();

        const reloaded = (this.portfolios || [])
            .flatMap(g => g.items)
            .find(p => p.id === created.id && p.userId === created.userId);

        if (reloaded) {
            this.selectedPortfolio = reloaded;
            setTimeout(() => this.onPortfolioSelected());
        }
    }

    @DetectMethodChanges({isAsync: true})
    async onDeleteIndependentPortfolio() {
        const portfolio = this.selectedPortfolio;

        // Guard: only independent, own-user books are deletable (see canDeletePortfolio).
        if (isVoid(portfolio) || !portfolio.isIndependent || !this.isOwnUserContext) {
            return;
        }

        try {
            // show() resolves on confirm, rejects on "No"/dismiss — mirror APG's pattern.
            await this.deletePortfolioDialog.show([
                'Are you sure you want to delete',
                `[${portfolio.name}]`
            ]);
        } catch {
            return;
        }

        // Remove from the independent-portfolios store (keyed by user).
        this._independentPortfoliosService.configure({
            userId: portfolio.userId,
            userName: portfolio.userName
        });
        this._independentPortfoliosService.deletePortfolio(portfolio.id);

        // Rebuild the dropdown without the deleted book.
        await this.initPortfolios();

        // CR-20260928-001: fall back to the own user's DEFAULT portfolio (flagged, else first by
        // name), mirroring APG's post-delete selection. The own APG list is never empty (the
        // portfolios service seeds one), so a valid fallback exists; downstream
        // onPortfolioSelected handlers dereference the portfolio without a null-guard.
        const ownUserId = this._sessionService.sessionData.userId;
        const fallback = findDefaultPortfolio((this.portfolios || [])
            .filter(g => g.userId === ownUserId)
            .flatMap(g => g.items)
            .filter(p => !p.isIndependent));

        if (fallback) {
            this.selectedPortfolio = fallback;
            setTimeout(() => this.onPortfolioSelected());
        }

        this._toastr.success(`[${portfolio.name}] portfolio has been deleted`, 'Portfolios');
    }

    private async loadAllPortfoliosForUser(userId: string, userName: string): Promise<ApgPortfolio[]> {
        const cfg: ServiceConfiguration = {
            orientation: undefined,
            userId,
            userName
        };
        this._portfoliosService.configure(cfg);
        // CR-20260928-001: every portfolio carries its own fixed asset; nothing to stamp.
        const apgPfs = this._portfoliosService.getPortfolios();

        this._independentPortfoliosService.configure({userId, userName});
        const independentPfs = this._independentPortfoliosService.getPortfolios();

        return apgPfs.concat(independentPfs);
    }

    refreshPortfolio(selectedPortfolio: ApgPortfolio) {
        this.portfolioSyncDialogCmp.show(selectedPortfolio, this);
    }

    getRefreshPortfolioCss() : Promise<string[]> {

        return this.hasOwnChanges()
            .then((hasOwnChanges) => {

                const hasTheirChanges = this.hasTheirChanges();

                const result = [];

                if (hasOwnChanges) {
                    result.push('own-changes');
                }

                if (hasTheirChanges) {
                    result.push('their-changes');
                }

                return result;

            });
    }

    hasTheirChanges(): boolean {

        if (isVoid(this.selectedPortfolio)) {
            return false;
        }

        if (this.selectedPortfolio.userId === this._sessionService.sessionData.userId) {
            return false;
        }

        if (this.isDataLoading) {
            return null;
        }

        const theirSnapshotPositions: HedgePosition[] = this._clientPositionsService
            .getTheirOriginalPositions(this.selectedPortfolio) || [];

        const theirCurrentPositions = this._clientPositionsService
            .getTheirCurrentPositions(this.selectedPortfolio) || [];

        if (isVoid(theirCurrentPositions)) {
            if (isVoid(theirSnapshotPositions)) {
                return false;
            }
        }

        const theirSnapshotIds = theirSnapshotPositions.map(x => `${x.ticker}|${x.qty}|${x.groupId}`);
        const theirCurrentIds = theirCurrentPositions.map(x => `${x.ticker}|${x.qty}|${x.groupId}`);

        const areEqual = arraysEqual(theirSnapshotIds, theirCurrentIds);

        return !areEqual;
    }

    async hasOwnChanges(): Promise<boolean> {

        if (isVoid(this.selectedPortfolio)) {
            return false;
        }

        if (this.selectedPortfolio.userId === this._sessionService.sessionData.userId) {
            return false;
        }

        if (this.isDataLoading) {
            return null;
        }

        const theirSnapshotPositions: HedgePosition[] = this._clientPositionsService
            .getTheirOriginalPositions(this.selectedPortfolio) || [];

        const currentPositions = (await this._hedgePositionsService
            .getHedgePositions(this.selectedPortfolio)) || [];

        if (isVoid(theirSnapshotPositions) && isVoid(currentPositions)) {
            return false;
        }

        if (isVoid(theirSnapshotPositions) && !isVoid(currentPositions)) {
            return true;
        }

        if (isVoid(currentPositions) && !isVoid(theirSnapshotPositions)) {
            return true;
        }

        const strings = theirSnapshotPositions.map(x => `${x.ticker}|${x.qty}|${x.groupId}`);

        const strings1 = currentPositions.map(x => `${x.ticker}|${x.qty}|${x.groupId}`);

        const areSame = arraysEqual(strings, strings1);

        return !areSame;
    }

    @DetectMethodChanges({isAsync: true})
    private async onPortfoliosUpdated(payload: { portfolios: ApgPortfolio[] }) {
        const selectedPortfolioId = this.selectedPortfolio?.id + this.selectedPortfolio?.userId;

        const updatedSelected = payload.portfolios.find(x =>  {
            const key = x.id + x.userId;
            return key === selectedPortfolioId;
        });

        try {
            this.isDataLoading = true;
            await this.initPortfolios();
            if (updatedSelected) {
                this.selectedPortfolio =updatedSelected;
            }
        } catch(e)  {
            console.log(e);
        } finally {
            this.isDataLoading = false;
        }
    }
}